import { useState, useEffect } from 'react';
import { officialRequestsAPI } from '@/lib/api';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { MailPlus, Loader2, Paperclip, X, Send, Upload } from 'lucide-react';
import { toast } from 'sonner';

const MAX_FILES = 8;
const MAX_SIZE_BYTES = 50 * 1024 * 1024;

const humanSize = (b) => {
    if (b < 1024) return `${b} o`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} Ko`;
    return `${(b / (1024 * 1024)).toFixed(1)} Mo`;
};

export default function NewOfficialRequestDialog({ open, onOpenChange, onCreated, prefillArea }) {
    const [config, setConfig] = useState(null);
    const [loadingConfig, setLoadingConfig] = useState(false);
    const [subjectArea, setSubjectArea] = useState('');
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [files, setFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoadingConfig(true);
        officialRequestsAPI.config()
            .then((res) => setConfig(res.data))
            .catch(() => toast.error("Impossible de charger la configuration des demandes officielles"))
            .finally(() => setLoadingConfig(false));
    }, [open]);

    // Pré-remplir le secteur si fourni (venant de l'organigramme)
    useEffect(() => {
        if (open && prefillArea) setSubjectArea(prefillArea);
    }, [open, prefillArea]);

    // Reset à la fermeture
    useEffect(() => {
        if (!open) {
            setSubjectArea('');
            setTitle('');
            setMessage('');
            setFiles([]);
        }
    }, [open]);

    const area = config?.subject_areas?.find(a => a.key === subjectArea);
    const dirigeantsMap = config?.dirigeants || {};
    const recipientLabels = (area?.recipients || []).map(k => dirigeantsMap[k]?.name || k);

    const addFiles = (fList) => {
        const arr = Array.from(fList || []);
        if (files.length + arr.length > MAX_FILES) {
            toast.error(`Maximum ${MAX_FILES} pièces jointes`);
            return;
        }
        for (const f of arr) {
            if (f.size > MAX_SIZE_BYTES) {
                toast.error(`"${f.name}" dépasse 50 Mo`);
                return;
            }
        }
        setFiles((prev) => [...prev, ...arr]);
    };

    const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

    const submit = async () => {
        if (!subjectArea) return toast.error("Choisissez un secteur");
        if (!title.trim()) return toast.error("Titre requis");
        if (!message.trim()) return toast.error("Message requis");
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append('subject_area', subjectArea);
            fd.append('title', title.trim());
            fd.append('message', message.trim());
            files.forEach((f) => fd.append('files', f, f.name));
            await officialRequestsAPI.create(fd);
            onCreated?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de l'envoi de la demande");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-official-request-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <MailPlus className="w-5 h-5 text-indigo-600" />
                        Nouvelle demande officielle
                    </DialogTitle>
                    <DialogDescription>
                        Rédigez une demande officielle à un dirigeant de Valnexo. Elle sera enregistrée
                        et le/les destinataires seront notifiés (in-app + email).
                    </DialogDescription>
                </DialogHeader>

                {loadingConfig ? (
                    <div className="py-8 text-center text-slate-500">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                        Chargement…
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Secteur */}
                        <div>
                            <Label htmlFor="or-subject">Secteur concerné <span className="text-red-500">*</span></Label>
                            <Select value={subjectArea} onValueChange={setSubjectArea}>
                                <SelectTrigger id="or-subject" data-testid="or-subject-select" className="mt-1">
                                    <SelectValue placeholder="Choisissez le secteur de votre demande…" />
                                </SelectTrigger>
                                <SelectContent className="max-h-[300px]">
                                    {(config?.subject_areas || []).map((a) => (
                                        <SelectItem key={a.key} value={a.key} data-testid={`or-area-${a.key}`}>
                                            {a.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {area && (
                                <div className="mt-2 flex items-center gap-2 flex-wrap text-xs text-slate-600">
                                    <span>Destinataire{recipientLabels.length > 1 ? 's' : ''} :</span>
                                    {recipientLabels.map((n, i) => (
                                        <Badge key={i} variant="secondary" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                                            {n}
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Titre */}
                        <div>
                            <Label htmlFor="or-title">Objet <span className="text-red-500">*</span></Label>
                            <Input
                                id="or-title"
                                data-testid="or-title-input"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Ex : Question sur le paiement de la prime de secteur"
                                maxLength={200}
                                className="mt-1"
                            />
                        </div>

                        {/* Message */}
                        <div>
                            <Label htmlFor="or-message">Message <span className="text-red-500">*</span></Label>
                            <Textarea
                                id="or-message"
                                data-testid="or-message-input"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Détaillez votre demande…"
                                rows={7}
                                className="mt-1"
                            />
                        </div>

                        {/* Pièces jointes */}
                        <div>
                            <Label>Pièces jointes <span className="text-slate-400 text-xs">(PDF, images, vidéos — 50 Mo max/fichier, {MAX_FILES} fichiers max)</span></Label>
                            <label
                                htmlFor="or-files"
                                className="mt-1 flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg py-4 cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/40 transition-colors text-sm text-slate-600"
                                data-testid="or-files-drop"
                            >
                                <Upload className="w-4 h-4" />
                                Cliquez pour sélectionner un ou plusieurs fichiers
                            </label>
                            <input
                                id="or-files"
                                type="file"
                                multiple
                                className="hidden"
                                onChange={(e) => addFiles(e.target.files)}
                                data-testid="or-files-input"
                            />
                            {files.length > 0 && (
                                <ul className="mt-2 space-y-1.5">
                                    {files.map((f, i) => (
                                        <li key={i} className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5">
                                            <Paperclip className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                            <span className="flex-1 truncate">{f.name}</span>
                                            <span className="text-xs text-slate-500">{humanSize(f.size)}</span>
                                            <button
                                                onClick={() => removeFile(i)}
                                                className="p-1 text-slate-400 hover:text-red-600 rounded"
                                                aria-label="Retirer"
                                                data-testid={`or-file-remove-${i}`}
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                        Annuler
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={submitting || loadingConfig || !subjectArea || !title.trim() || !message.trim()}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        data-testid="or-submit-btn"
                    >
                        {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                        Envoyer la demande
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
